import {Folio} from "crqlar-react-common/dist/models/billing/folio.model";
import {useQuery} from "react-query";
import {DigitalSigningListRequest, DigitalSigningStatusView} from "../digitalSigning.model";
import {DigitalSigningService} from "../digitalSigning.service";
import {DIGITAL_SIGNING_STALE_TIME, digitalSigningFolioKey} from "../digitalSigningQueries";

/**
 * A folio's latest signing, whatever its status - only a folio of a reservation or of a
 * package (its group's) has one. The folio dialog reads it ahead with the folio's lines and its folio
 * view keeps it current (the same key and fetcher), so the folio list's rows cost no read of their own.
 */
export const useLatestFolioDigitalSigning = (folio: Pick<Folio, 'id' | 'reservationId' | 'reservationGroupId'>) => useQuery(
    digitalSigningFolioKey(folio.id),
    () => DigitalSigningService.latestForFolio(folio.id!),
    {
        enabled: !!folio.id && (!!folio.reservationId || !!folio.reservationGroupId),
        staleTime: DIGITAL_SIGNING_STALE_TIME,
        refetchOnWindowFocus: false,
    }
);

const latestOf = async (request: DigitalSigningListRequest) => (
    (await DigitalSigningService.list({...request, limit: {limit: 1, offset: 0}})).content?.[0]
);

// The one whose link was issued last
const newer = (a?: DigitalSigningStatusView, b?: DigitalSigningStatusView) => {
    if (!a || !b) return a ?? b;
    return new Date(b.issuedAt ?? 0).getTime() > new Date(a.issuedAt ?? 0).getTime() ? b : a;
};

/**
 * A reservation's latest signing, whatever its status - the reservation dialog and its
 * settings page read it whatever the templates offer now: a link signed or confirmed before its sector
 * was taken off them still counts. A member of the package [packageGroupId] gets the package's too -
 * its one folio has no reservation, so it is found by the group -, whichever was issued last. Nothing
 * tells cheaply that none can exist, so it is read for every saved reservation.
 */
export const useLatestReservationDigitalSigning = (reservationId: string | undefined, packageGroupId?: string) => useQuery(
    ['digitalSignings', 'reservation', reservationId, packageGroupId],
    async () => {
        // The list narrows by every filter it gets: the reservation's and the package's are two reads
        const [own, ofPackage] = await Promise.all([
            latestOf({reservationIds: [reservationId!]}),
            packageGroupId ? latestOf({reservationGroupIds: [packageGroupId]}) : undefined,
        ]);
        return newer(own, ofPackage);
    },
    {enabled: !!reservationId, refetchOnWindowFocus: false}
);
